// LegalPage — shared template for Privacy Policy, Terms of Service, and Cookie
// Policy. Resolves the document from the URL path via getLegalDoc(). Routed at
// /privacy, /terms, and /cookies.

import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import Navbar from "../components/homepage/Navbar";
import Footer from "../components/homepage/Footer";
import PageHero from "../components/shared/PageHero";
import NotFoundPage from "./NotFoundPage";
import { getLegalDoc } from "../data/legalData";

export default function LegalPage() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, "").split("/")[0];
  const doc = getLegalDoc(slug);

  if (!doc) return <NotFoundPage />;

  const pageUrl = `https://lumotis.com/${doc.slug}`;
  const docs = [
    { slug: "privacy", label: "Privacy Policy" },
    { slug: "terms", label: "Terms of Service" },
    { slug: "cookies", label: "Cookie Policy" },
  ];

  return (
    <div className="bg-white text-charcoal min-h-screen">
      <Helmet>
        <title>{`${doc.title} | Lumotis`}</title>
        <meta
          name="description"
          content={`${doc.title} for Lumotis. ${doc.intro.slice(0, 150)}`}
        />
        <link rel="canonical" href={pageUrl} />
        <meta property="og:title" content={`${doc.title} | Lumotis`} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
      </Helmet>

      <Navbar />

      <PageHero
        eyebrow={doc.eyebrow}
        title={doc.title}
        breadcrumb={[{ label: "Home", href: "/" }, { label: doc.title }]}
        subtitle={`Last updated: ${doc.updated}`}
      />

      <section className="py-16 md:py-20 bg-white">
        <div className="container grid lg:grid-cols-12 gap-12">
          {/* Sidebar nav */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-28">
              <p className="text-overline text-zinc-500 mb-4">Legal</p>
              <nav className="flex flex-col gap-1">
                {docs.map((d) => (
                  <a
                    key={d.slug}
                    href={`/${d.slug}`}
                    className={`px-3 py-2 rounded-xl text-sm font-semibold transition-colors ${
                      d.slug === doc.slug
                        ? "bg-electric text-white"
                        : "text-charcoal hover:bg-zinc-50"
                    }`}
                  >
                    {d.label}
                  </a>
                ))}
              </nav>
            </div>
          </aside>

          {/* Body */}
          <article className="lg:col-span-9 max-w-3xl">
            <div className="flex items-start gap-3 border border-electric/20 bg-electric/5 rounded-2xl p-5 mb-10">
              <ShieldCheck className="w-5 h-5 text-electric mt-0.5 shrink-0" />
              <p className="text-[15px] leading-relaxed text-zinc-700">
                {doc.intro}
              </p>
            </div>

            {doc.sections.map((s, i) => (
              <motion.div
                key={s.heading}
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.35, delay: Math.min(i * 0.03, 0.2) }}
                className="mb-9"
              >
                <h2 className="font-display text-xl md:text-2xl font-bold text-charcoal mb-3">
                  {s.heading}
                </h2>
                {s.body &&
                  s.body.map((p, j) => (
                    <p
                      key={j}
                      className="text-[15px] leading-relaxed text-zinc-600 mb-3"
                    >
                      {p}
                    </p>
                  ))}
                {s.list && (
                  <ul className="mt-2 space-y-2">
                    {s.list.map((li, j) => (
                      <li
                        key={j}
                        className="flex gap-3 text-[15px] leading-relaxed text-zinc-600"
                      >
                        <span className="mt-2 w-1.5 h-1.5 rounded-full bg-electric shrink-0" />
                        <span>{li}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </motion.div>
            ))}

            <p className="mt-12 text-sm text-zinc-400 border-t border-zinc-100 pt-6">
              This document is provided for general informational purposes and
              does not constitute legal advice.
            </p>
          </article>
        </div>
      </section>

      <Footer />
    </div>
  );
}
